import dagre from '@dagrejs/dagre';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 56;
const NODE_SEP = 48;
const RANK_SEP = 120;

/**
 * Un cluster affiche ses noms en grille (colonnes fixes), chacun dans sa
 * propre mini-carte — pas un empilement vertical, trop haut dès que le
 * nombre de personnes grandit. Constantes exportées : le composant Node doit
 * utiliser les mêmes valeurs pour que sa taille réelle corresponde à celle
 * réservée ici par dagre.
 */
export const LEAF_CLUSTER_COLUMNS = 4;
export const LEAF_CLUSTER_CELL_WIDTH = 140;
export const LEAF_CLUSTER_GAP = 18;
export const LEAF_CLUSTER_PADDING = 12;
const LEAF_CLUSTER_CELL_HEIGHT = 36;

/**
 * En dessous de ce nombre de feuilles, pas de cluster : les arêtes
 * convergentes ne posent pas de problème visuel, autant garder des nœuds
 * standalone classiques (poignée + corbeille sur l'arête, promotion en
 * manager sans étape intermédiaire).
 */
const LEAF_CLUSTER_MIN_COUNT = 4;

/** Préfixe impossible à collisionner avec un vrai id métier. */
const CLUSTER_ID_PREFIX = ' cluster ';

export interface TreeFlowLayoutNode {
  id: string;
  parentId: string | null;
}

export type TreeFlowPosition = { x: number; y: number };

export interface TreeFlowLayoutResult {
  positions: Map<string, TreeFlowPosition>;
  /** Feuilles clusterisées par parent — cf. `clusterLeafChildren`. */
  leafClusters: Map<string, string[]>;
}

interface TreeFlowLayoutOptions {
  nodeWidth?: number;
  nodeHeight?: number;
  /** Espacement entre nœuds d'un même niveau (dagre `nodesep`). */
  horizontalGap?: number;
  /** Espacement entre niveaux (dagre `ranksep`). */
  verticalGap?: number;
  /** 'vertical' (défaut, rankdir TB) ou 'horizontal' (rankdir LR). */
  orientation?: 'vertical' | 'horizontal';
  /** Seuil de clustering des feuilles (défaut `LEAF_CLUSTER_MIN_COUNT`). `Infinity` désactive le clustering. */
  leafClusterMinCount?: number;
}

/** Id du nœud cluster regroupant les feuilles directes d'un parent. */
export function leafClusterNodeId(parentId: string): string {
  return `${CLUSTER_ID_PREFIX}${parentId}`;
}

/**
 * Un id de nœud cluster ne correspond à aucune personne réelle : quand une
 * connexion démarre ou arrive dessus, l'id de la vraie carte concernée est
 * porté par `connection.sourceHandle`/`targetHandle`, pas par
 * `connection.source`/`target` — à l'appelant de vérifier ce cas via cette
 * fonction pour résoudre le bon id.
 */
export function isLeafClusterNodeId(id: string): boolean {
  return id.startsWith(CLUSTER_ID_PREFIX);
}

/** Largeur (fixe, dépend uniquement du nombre de colonnes) d'un cluster. */
export function leafClusterNodeWidth(): number {
  return (
    LEAF_CLUSTER_COLUMNS * LEAF_CLUSTER_CELL_WIDTH +
    (LEAF_CLUSTER_COLUMNS - 1) * LEAF_CLUSTER_GAP +
    LEAF_CLUSTER_PADDING * 2
  );
}

/** Hauteur d'un cluster pour un nombre de cartes donné. */
export function leafClusterNodeHeight(leafCount: number): number {
  const rows = Math.ceil(leafCount / LEAF_CLUSTER_COLUMNS);
  return rows * LEAF_CLUSTER_CELL_HEIGHT + (rows - 1) * LEAF_CLUSTER_GAP + LEAF_CLUSTER_PADDING * 2;
}

/**
 * Regroupe, par parent, ses enfants directs sans descendance propre
 * (feuilles) à partir de `minCount` d'entre elles — affichées en un seul
 * cluster plutôt qu'en autant de nœuds/arêtes convergentes sur le parent.
 * Les enfants ayant eux-mêmes des enfants (branches) ne sont jamais
 * clusterisés : ils gardent un nœud et un lien direct classiques, avec la
 * place pour leur propre sous-arbre.
 */
export function clusterLeafChildren(
  nodes: readonly TreeFlowLayoutNode[],
  minCount: number = LEAF_CLUSTER_MIN_COUNT
): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>();
  for (const node of nodes) {
    if (node.parentId === null) continue;
    const siblings = childrenByParent.get(node.parentId) ?? [];
    siblings.push(node.id);
    childrenByParent.set(node.parentId, siblings);
  }

  const isLeaf = (id: string) => (childrenByParent.get(id) ?? []).length === 0;

  const leafClusters = new Map<string, string[]>();
  for (const [parentId, children] of childrenByParent) {
    const leaves = children.filter(isLeaf);
    if (leaves.length >= minCount) leafClusters.set(parentId, leaves);
  }
  return leafClusters;
}

/**
 * Positionne une forêt via dagre (même lib que l'exemple officiel React
 * Flow). Les feuilles clusterisées (cf. `clusterLeafChildren`) ne reçoivent
 * pas de position individuelle : c'est le cluster de leur parent (id
 * `leafClusterNodeId`) qui est positionné, à charge pour l'appelant d'en
 * afficher le contenu (liste de noms) dans un unique nœud.
 */
export function layoutTreeForest(
  nodes: readonly TreeFlowLayoutNode[],
  options: TreeFlowLayoutOptions = {}
): TreeFlowLayoutResult {
  const nodeWidth = options.nodeWidth ?? NODE_WIDTH;
  const nodeHeight = options.nodeHeight ?? NODE_HEIGHT;
  const nodesep = options.horizontalGap ?? NODE_SEP;
  const ranksep = options.verticalGap ?? RANK_SEP;
  const rankdir = options.orientation === 'horizontal' ? 'LR' : 'TB';

  const leafClusters = clusterLeafChildren(nodes, options.leafClusterMinCount);
  const clusteredLeafIds = new Set<string>();
  for (const leaves of leafClusters.values()) {
    for (const leafId of leaves) clusteredLeafIds.add(leafId);
  }

  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir, nodesep, ranksep });

  for (const node of nodes) {
    if (clusteredLeafIds.has(node.id)) continue; // représentée par le cluster de son parent
    graph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  }

  const clusterWidth = leafClusterNodeWidth();

  // Le cluster est affiché centré sur le même centre de rang que ses frères
  // classiques (cf. plus bas) : dagre réserve donc sa taille réelle telle
  // quelle, sans marge supplémentaire — le rendu correspond exactement à ce
  // que dagre a prévu, pas de risque de chevauchement avec le rang suivant.
  for (const [parentId, leaves] of leafClusters) {
    graph.setNode(leafClusterNodeId(parentId), {
      width: clusterWidth,
      height: leafClusterNodeHeight(leaves.length)
    });
    graph.setEdge(parentId, leafClusterNodeId(parentId));
  }

  for (const node of nodes) {
    if (node.parentId === null || clusteredLeafIds.has(node.id)) continue;
    graph.setEdge(node.parentId, node.id);
  }

  dagre.layout(graph);

  const positions = new Map<string, TreeFlowPosition>();

  for (const node of nodes) {
    if (clusteredLeafIds.has(node.id)) continue;
    const graphNode = graph.node(node.id);
    if (!graphNode) continue;
    positions.set(node.id, { x: graphNode.x - nodeWidth / 2, y: graphNode.y - nodeHeight / 2 });
  }

  for (const [parentId, leaves] of leafClusters) {
    const clusterId = leafClusterNodeId(parentId);
    const graphNode = graph.node(clusterId);
    if (!graphNode) continue;
    const height = leafClusterNodeHeight(leaves.length);
    positions.set(clusterId, { x: graphNode.x - clusterWidth / 2, y: graphNode.y - height / 2 });
  }

  return { positions, leafClusters };
}
